/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/hooks";
import {
  fetchInvitations,
  addInvitation,
  deleteInvitation,
  fetchOrganizationAccess,
  deleteAccess,
  fetchUserOrganizations,
  fetchOrganizationTables,
  fetchPaidOrdersByOrganization,
  fetchOrganizationContent,
  fetchOrganization,
  fetchOrganizationAvatars,
  fetchOrganizationWebsites,
  fetchOrganizationOrders,
} from "./slice";
import { Invitation, Organization, User } from "@/types";

export interface OrganizationFetchConfig {
  access?: boolean;
  tables?: boolean;
  content?: boolean;
  avatars?: boolean;
  branches?: boolean;
  orders?: boolean;
  websites?: boolean;
  menus?: boolean;
  faqs?: boolean;
  categories?: boolean;
}

const useOrganizations = () => {
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);

  const dispatch = useAppDispatch();
  const organizations = useAppSelector((store) => store.organizations) as unknown as Record<Organization["id"], Organization>;

  const currentOrganization = useMemo(() => {
    return organizations[organizationId ?? ""] ?? null;
  }, [organizations, organizationId]);

  const getOrganizations = useCallback(async () => {
    await dispatch(fetchUserOrganizations());
  }, []);

  const getOrganizationWebsites = useCallback(async () => {
    if (!organizationId) return;
    await dispatch(fetchOrganizationWebsites(organizationId));
  }, [organizationId]);

  const getOrgDetails = useCallback(
    async (config: OrganizationFetchConfig) => {
      const defaultConfig = {
        access: false,
        tables: false,
        content: false,
        avatars: false,
        orders: false,
        websites: false,
        menus: false,
        faqs: false,
        categories: false,
        branches: false,
        ...config,
      };

      if (organizationId) {
        dispatch(fetchOrganization(organizationId));

        if (defaultConfig.access) dispatch(fetchOrganizationAccess(organizationId));
        if (defaultConfig.tables) dispatch(fetchOrganizationTables(organizationId));
        if (defaultConfig.content) dispatch(fetchOrganizationContent(organizationId));
        if (defaultConfig.avatars) dispatch(fetchOrganizationAvatars(organizationId));
        if (defaultConfig.websites) dispatch(fetchOrganizationWebsites(organizationId));
        if (defaultConfig.orders) dispatch(fetchOrganizationOrders(organizationId));        
      }
    },
    [organizationId]
  );

  const deleteOrgAccess = async (uid: User["uid"]) => {
    if (!organizationId || !uid) return;
    await dispatch(deleteAccess({ organizationId, uid }));
  };

  const fetchAccessInvitations = useCallback(async () => {
    if (!organizationId) return;
    await dispatch(fetchInvitations(organizationId));
  }, [organizationId]);

  const addAccessInvitation = async (invitation: Invitation) => {
    if (!organizationId) return;
    dispatch(addInvitation({ organizationId, invitation }));
  };

  const deleteAccessInvitation = async (invitationId: Invitation["id"]) => {
    if (!invitationId || !organizationId) return;
    dispatch(deleteInvitation({ organizationId, invitationId }));
  };

  const getPaidOrders = useCallback(async () => {
    if (!organizationId) return;
    await dispatch(fetchPaidOrdersByOrganization(organizationId));
  }, [organizationId]);

  // TODO: Add getAssignedOrders here

  const getAvatars = useCallback(async () => {
    if (!organizationId) return;
    await dispatch(fetchOrganizationAvatars(organizationId));
  }, [organizationId]);

  return {
    organizations,
    currentOrganization,
    getOrganizations,
    getOrgDetails,
    deleteOrgAccess,
    fetchAccessInvitations,
    addAccessInvitation,
    deleteAccessInvitation,
    getPaidOrders,
    getAvatars,
    getOrganizationWebsites,
  };
};

export default useOrganizations;
